import type { PluralForms } from './plural';

export type Language = 'en' | 'uk' | 'de';

export const translations = {
  en: {
    appName: 'ShopSmart AI',
    appDescription: 'AI meal & shopping planner with real store prices',
    selectSupermarkets: 'Select Supermarkets',
    familySize: 'Family Size',
    dietaryPreferences: 'Dietary Preferences',
    additionalPreferences: 'Additional Preferences',
    preferencesPlaceholder: 'E.g., no pork, prefer organic vegetables, need baby food...',
    generateList: 'Generate Shopping List',
    generating: 'Generating...',
    vegetarian: 'Vegetarian',
    vegan: 'Vegan',
    glutenFree: 'Gluten-Free',
    lactoseFree: 'Lactose-Free',
    highProtein: 'High Protein',
    lowCarb: 'Low Carb',
    familyFriendly: 'Family Friendly',
    organic: 'Organic',
    budgetSaver: 'Budget Saver',
    yourShoppingList: 'Your Shopping List',
    estimatedCost: 'Estimated Cost',
    budgetUsed: 'Budget Used',
    copyToClipboard: 'Copy to Clipboard',
    print: 'Print',
    newList: 'New List',
    completed: 'Completed',
    vegetables: 'Vegetables',
    fruits: 'Fruits',
    meat: 'Meat',
    fish: 'Fish',
    dairy: 'Dairy',
    bread: 'Bread & Bakery',
    beverages: 'Beverages',
    snacks: 'Snacks',
    frozen: 'Frozen',
    pantry: 'Pantry',
    cleaning: 'Cleaning',
    hygiene: 'Hygiene',
    other: 'Other',
    error: 'Error',
    loadFailed: 'Load failed',
    dismiss: 'Dismiss',
    refreshPage: 'Refresh Page',
    errorTimeout: 'The AI service took too long to respond. Please try again.',
    errorRateLimited: 'Too many requests. Please wait a minute and try again.',
    errorDailyLimit:
      'The daily limit for the AI service has been reached. Please try again tomorrow.',
    errorAiUnavailable: 'The AI service is unavailable right now. Please try again later.',
    errorGenerationFailed: 'Could not generate a plan. Please try again.',
    errorValidation: 'Some of the entered values are invalid. Please check the form.',
    builtWith: 'Built with',
    using: 'using React, TypeScript, FastAPI & OpenAI',
    diplomaProject: 'Diploma Project by Ivan Sytnik (KH-M524)',
    supervisor: 'Supervisor: Kharchenko A.O.',
    university: 'NTU "KhPI"',
    aiCreating: 'AI is creating your shopping list...',
    analyzingPrices: 'Analyzing prices and optimizing for your budget',
    didYouKnow: 'Did you know?',
    funFact:
      'ShopSmart AI considers current German supermarket prices and distributes items based on typical pricing at each store.',
    history: 'History',
    savedLists: 'Saved Lists',
    saveList: 'Save List',
    noSavedLists: 'No saved lists yet',
    loadList: 'Load',
    deleteList: 'Delete',
    listSaved: 'List saved!',
    clearHistory: 'Clear All',
    calories: 'Calories',
    protein: 'Protein',
    fat: 'Fat',
    carbs: 'Carbs',
    nutritionPerDay: 'Daily Nutrition',
    perPerson: 'per person',
    dailyNorm: 'of daily norm',
    nutritionNote: 'Values are estimates based on typical product nutrition.',
    // Menu planning
    mode: 'Mode',
    shoppingList: 'Shopping List',
    mealPlanning: 'Meal Planning',
    days: 'Days',
    day: 'Day',
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    snack: 'Snack',
    generateMenu: 'Generate Menu',
    yourMealPlan: 'Your Meal Plan',
    productsForMenu: 'Products for this menu',
    aiCreatingMenu: 'AI is creating your meal plan...',
    planningMeals: 'Planning balanced meals for your family',
    // v2: catalog-driven form
    country: 'Country',
    countryAT: 'Austria',
    countryDE: 'Germany',
    loadingStores: 'Loading stores…',
    metaLoadFailed: 'Could not load the store list.',
    retry: 'Retry',
    noStores: 'No stores are available yet.',
    storesHint: 'Choose 1–{max} stores',
    budgetInvalid: 'Enter a budget between €{min} and €{max}.',
    // v2: results
    total: 'Total',
    budget: 'Budget',
    budgetForDays: 'Budget for {days}',
    budgetWeekly: 'Weekly budget',
    budgetLeft: '€{amount} left',
    purchasedKcal: 'Purchased food ≈ {kcal} kcal per person/day',
    menuKcal: 'Menu ≈ {kcal} kcal per person/day',
    pricesAsOf: 'Reference prices as of {date}',
    warningLowKcal:
      'This menu provides less food than recommended (≈ {kcal} kcal per person/day). Try a larger budget or fewer days.',
    warningLowKcalShopping:
      'This list buys less food than recommended for a week (≈ {kcal} kcal per person/day). Try a larger budget.',
    warningKcalToppedUp:
      'We added filling staples from the leftover budget so the list covers more of the week.',
    warningBudgetTrimmed: 'Some products were reduced or removed to stay within your budget:',
    trimmedReduced: 'reduced {from}→{to}',
    trimmedRemoved: 'removed',
    removedBudget: 'removed (budget)',
    partiallyBought: 'only {to} of {from} packs bought (budget)',
    tip: 'Tip',
    perPack: 'per pack',
    unitKg: 'kg',
    unitL: 'l',
    unitG: 'g',
    unitMl: 'ml',
    unitPcs: 'pcs',
    unitPack: 'pack',
    errorUnknownStore:
      'A selected store is not available for this country. Please choose the stores again.',
    errorNotEnoughProducts:
      'Too few products match these stores and diets. Add another store, or remove a diet.',
    errorNetwork: 'Cannot reach the server. Check your connection and try again.',
    // Share link
    share: 'Share',
    shareCopied: 'Link copied to clipboard.',
    shareTooLarge:
      'This plan is too large to share as a link. Use Copy to Clipboard or Print instead.',
    shareFailed: 'Could not copy the link.',
    sharedPlan: 'Shared plan (read-only)',
    createYourOwn: 'Create your own',
    sharedInvalid: 'This share link is invalid or was made by an older version of the app.',
  },

  uk: {
    appName: 'ShopSmart AI',
    appDescription: 'ШІ-планувальник меню та покупок з реальними цінами магазинів',
    selectSupermarkets: 'Оберіть супермаркети',
    familySize: "Розмір сім'ї",
    dietaryPreferences: 'Дієтичні переваги',
    additionalPreferences: 'Додаткові побажання',
    preferencesPlaceholder: 'Напр., без свинини, перевага органічним овочам...',
    generateList: 'Згенерувати список',
    generating: 'Генерація...',
    vegetarian: 'Вегетаріанське',
    vegan: 'Веганське',
    glutenFree: 'Без глютену',
    lactoseFree: 'Без лактози',
    highProtein: 'Високобілкове',
    lowCarb: 'Низьковуглеводне',
    familyFriendly: "Для сім'ї",
    organic: 'Органічне',
    budgetSaver: 'Економне',
    yourShoppingList: 'Ваш список покупок',
    estimatedCost: 'Орієнтовна вартість',
    budgetUsed: 'Використано бюджету',
    copyToClipboard: 'Копіювати',
    print: 'Друк',
    newList: 'Новий список',
    completed: 'Виконано',
    vegetables: 'Овочі',
    fruits: 'Фрукти',
    meat: "М'ясо",
    fish: 'Риба',
    dairy: 'Молочні продукти',
    bread: 'Хліб та випічка',
    beverages: 'Напої',
    snacks: 'Снеки',
    frozen: 'Заморожені',
    pantry: 'Бакалія',
    cleaning: 'Засоби для прибирання',
    hygiene: 'Гігієна',
    other: 'Інше',
    error: 'Помилка',
    loadFailed: 'Не вдалося завантажити',
    dismiss: 'Закрити',
    refreshPage: 'Оновити сторінку',
    errorTimeout: 'Сервіс ШІ відповідав надто довго. Спробуйте ще раз.',
    errorRateLimited: 'Забагато запитів. Зачекайте хвилину й спробуйте ще раз.',
    errorDailyLimit: 'Денний ліміт сервісу ШІ вичерпано. Спробуйте завтра.',
    errorAiUnavailable: 'Сервіс ШІ зараз недоступний. Спробуйте пізніше.',
    errorGenerationFailed: 'Не вдалося створити план. Спробуйте ще раз.',
    errorValidation: 'Деякі введені значення некоректні. Перевірте форму.',
    builtWith: 'Створено з',
    using: 'за допомогою React, TypeScript, FastAPI та OpenAI',
    diplomaProject: 'Дипломний проект Івана Ситніка (КН-М524)',
    supervisor: 'Керівник: Харченко А.О.',
    university: 'НТУ "ХПІ"',
    aiCreating: 'ШІ створює ваш список покупок...',
    analyzingPrices: 'Аналізуємо ціни та оптимізуємо під ваш бюджет',
    didYouKnow: 'Чи знали ви?',
    funFact: 'ShopSmart AI враховує актуальні ціни німецьких супермаркетів.',
    history: 'Історія',
    savedLists: 'Збережені списки',
    saveList: 'Зберегти список',
    noSavedLists: 'Немає збережених списків',
    loadList: 'Завантажити',
    deleteList: 'Видалити',
    listSaved: 'Список збережено!',
    clearHistory: 'Очистити все',
    calories: 'Калорії',
    protein: 'Білки',
    fat: 'Жири',
    carbs: 'Вуглеводи',
    nutritionPerDay: 'Денна норма',
    perPerson: 'на людину',
    dailyNorm: 'денної норми',
    nutritionNote: 'Значення є приблизними.',
    // Menu planning
    mode: 'Режим',
    shoppingList: 'Список покупок',
    mealPlanning: 'Планування меню',
    days: 'Днів',
    day: 'День',
    breakfast: 'Сніданок',
    lunch: 'Обід',
    dinner: 'Вечеря',
    snack: 'Перекус',
    generateMenu: 'Згенерувати меню',
    yourMealPlan: 'Ваш план харчування',
    productsForMenu: 'Продукти для цього меню',
    aiCreatingMenu: 'ШІ створює ваш план харчування...',
    planningMeals: "Плануємо збалансоване харчування для вашої сім'ї",
    // v2: catalog-driven form
    country: 'Країна',
    countryAT: 'Австрія',
    countryDE: 'Німеччина',
    loadingStores: 'Завантажуємо магазини…',
    metaLoadFailed: 'Не вдалося завантажити список магазинів.',
    retry: 'Повторити',
    noStores: 'Поки що немає доступних магазинів.',
    storesHint: 'Оберіть від 1 до {max} магазинів',
    budgetInvalid: 'Введіть бюджет від €{min} до €{max}.',
    // v2: results
    total: 'Разом',
    budget: 'Бюджет',
    budgetForDays: 'Бюджет на {days}',
    budgetWeekly: 'Бюджет на тиждень',
    budgetLeft: 'Залишок €{amount}',
    purchasedKcal: 'Куплені продукти ≈ {kcal} ккал на людину в день',
    menuKcal: 'Меню ≈ {kcal} ккал на людину в день',
    pricesAsOf: 'Довідкові ціни станом на {date}',
    warningLowKcal:
      'Це меню дає менше їжі, ніж рекомендовано (≈ {kcal} ккал на людину в день). Спробуйте більший бюджет або менше днів.',
    warningLowKcalShopping:
      'Цей список містить менше їжі, ніж рекомендовано на тиждень (≈ {kcal} ккал на людину в день). Спробуйте більший бюджет.',
    warningKcalToppedUp:
      'Ми додали ситні базові продукти із залишку бюджету, щоб список покривав більше тижня.',
    warningBudgetTrimmed: 'Щоб вкластися в бюджет, деякі продукти зменшено або прибрано:',
    trimmedReduced: 'зменшено {from}→{to}',
    trimmedRemoved: 'прибрано',
    removedBudget: 'прибрано (бюджет)',
    partiallyBought: 'куплено лише {to} з {from} уп. (бюджет)',
    tip: 'Порада',
    perPack: 'за уп.',
    unitKg: 'кг',
    unitL: 'л',
    unitG: 'г',
    unitMl: 'мл',
    unitPcs: 'шт.',
    unitPack: 'уп.',
    errorUnknownStore: 'Обраний магазин недоступний для цієї країни. Оберіть магазини ще раз.',
    errorNotEnoughProducts:
      'Замало продуктів для цих магазинів і дієт. Додайте ще один магазин або приберіть дієту.',
    errorNetwork: 'Не вдалося зʼєднатися з сервером. Перевірте зʼєднання та спробуйте ще раз.',
    // Share link
    share: 'Поділитися',
    shareCopied: 'Посилання скопійовано.',
    shareTooLarge:
      'Цей план завеликий для посилання. Скористайтеся кнопкою «Копіювати» або «Друк».',
    shareFailed: 'Не вдалося скопіювати посилання.',
    sharedPlan: 'Спільний план (лише перегляд)',
    createYourOwn: 'Створити власний',
    sharedInvalid: 'Це посилання недійсне або створене старішою версією застосунку.',
  },

  de: {
    appName: 'ShopSmart AI',
    appDescription: 'KI-Menü- und Einkaufsplaner mit echten Supermarktpreisen',
    selectSupermarkets: 'Supermärkte auswählen',
    familySize: 'Familiengröße',
    dietaryPreferences: 'Ernährungspräferenzen',
    additionalPreferences: 'Zusätzliche Wünsche',
    preferencesPlaceholder: 'Z.B. kein Schweinefleisch, Bio-Gemüse bevorzugt...',
    generateList: 'Einkaufsliste erstellen',
    generating: 'Wird erstellt...',
    vegetarian: 'Vegetarisch',
    vegan: 'Vegan',
    glutenFree: 'Glutenfrei',
    lactoseFree: 'Laktosefrei',
    highProtein: 'Proteinreich',
    lowCarb: 'Low Carb',
    familyFriendly: 'Familienfreundlich',
    organic: 'Bio',
    budgetSaver: 'Sparpreis',
    yourShoppingList: 'Ihre Einkaufsliste',
    estimatedCost: 'Geschätzte Kosten',
    budgetUsed: 'Budget verwendet',
    copyToClipboard: 'Kopieren',
    print: 'Drucken',
    newList: 'Neue Liste',
    completed: 'Erledigt',
    vegetables: 'Gemüse',
    fruits: 'Obst',
    meat: 'Fleisch',
    fish: 'Fisch',
    dairy: 'Milchprodukte',
    bread: 'Brot & Backwaren',
    beverages: 'Getränke',
    snacks: 'Snacks',
    frozen: 'Tiefkühl',
    pantry: 'Vorratskammer',
    cleaning: 'Reinigung',
    hygiene: 'Hygiene',
    other: 'Sonstiges',
    error: 'Fehler',
    loadFailed: 'Laden fehlgeschlagen',
    dismiss: 'Schließen',
    refreshPage: 'Seite aktualisieren',
    errorTimeout: 'Der KI-Dienst hat zu lange gebraucht. Bitte versuchen Sie es erneut.',
    errorRateLimited:
      'Zu viele Anfragen. Bitte warten Sie eine Minute und versuchen Sie es erneut.',
    errorDailyLimit:
      'Das Tageslimit des KI-Dienstes ist erreicht. Bitte versuchen Sie es morgen erneut.',
    errorAiUnavailable:
      'Der KI-Dienst ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.',
    errorGenerationFailed: 'Der Plan konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    errorValidation: 'Einige Eingaben sind ungültig. Bitte überprüfen Sie das Formular.',
    builtWith: 'Erstellt mit',
    using: 'mit React, TypeScript, FastAPI & OpenAI',
    diplomaProject: 'Diplomprojekt von Ivan Sytnik (KH-M524)',
    supervisor: 'Betreuer: Kharchenko A.O.',
    university: 'NTU "KhPI"',
    aiCreating: 'KI erstellt Ihre Einkaufsliste...',
    analyzingPrices: 'Preise werden analysiert und optimiert',
    didYouKnow: 'Wussten Sie?',
    funFact: 'ShopSmart AI berücksichtigt aktuelle deutsche Supermarktpreise.',
    history: 'Verlauf',
    savedLists: 'Gespeicherte Listen',
    saveList: 'Liste speichern',
    noSavedLists: 'Keine gespeicherten Listen',
    loadList: 'Laden',
    deleteList: 'Löschen',
    listSaved: 'Liste gespeichert!',
    clearHistory: 'Alles löschen',
    calories: 'Kalorien',
    protein: 'Eiweiß',
    fat: 'Fett',
    carbs: 'Kohlenhydrate',
    nutritionPerDay: 'Tägliche Ernährung',
    perPerson: 'pro Person',
    dailyNorm: 'der Tagesnorm',
    nutritionNote: 'Die Werte sind Schätzungen.',
    // Menu planning
    mode: 'Modus',
    shoppingList: 'Einkaufsliste',
    mealPlanning: 'Menüplanung',
    days: 'Tage',
    day: 'Tag',
    breakfast: 'Frühstück',
    lunch: 'Mittagessen',
    dinner: 'Abendessen',
    snack: 'Snack',
    generateMenu: 'Menü erstellen',
    yourMealPlan: 'Ihr Menüplan',
    productsForMenu: 'Produkte für dieses Menü',
    aiCreatingMenu: 'KI erstellt Ihren Menüplan...',
    planningMeals: 'Ausgewogene Mahlzeiten für Ihre Familie planen',
    // v2: catalog-driven form
    country: 'Land',
    countryAT: 'Österreich',
    countryDE: 'Deutschland',
    loadingStores: 'Geschäfte werden geladen…',
    metaLoadFailed: 'Die Liste der Geschäfte konnte nicht geladen werden.',
    retry: 'Erneut versuchen',
    noStores: 'Noch keine Geschäfte verfügbar.',
    storesHint: '1–{max} Geschäfte auswählen',
    budgetInvalid: 'Bitte ein Budget zwischen €{min} und €{max} eingeben.',
    // v2: results
    total: 'Summe',
    budget: 'Budget',
    budgetForDays: 'Budget für {days}',
    budgetWeekly: 'Wochenbudget',
    budgetLeft: '€{amount} übrig',
    purchasedKcal: 'Gekaufte Lebensmittel ≈ {kcal} kcal pro Person/Tag',
    menuKcal: 'Menü ≈ {kcal} kcal pro Person/Tag',
    pricesAsOf: 'Referenzpreise vom {date}',
    warningLowKcal:
      'Dieses Menü liefert weniger Essen als empfohlen (≈ {kcal} kcal pro Person/Tag). Versuchen Sie ein größeres Budget oder weniger Tage.',
    warningLowKcalShopping:
      'Diese Liste enthält weniger Essen als für eine Woche empfohlen (≈ {kcal} kcal pro Person/Tag). Versuchen Sie ein größeres Budget.',
    warningKcalToppedUp:
      'Wir haben vom restlichen Budget sättigende Grundnahrungsmittel ergänzt, damit die Liste mehr von der Woche abdeckt.',
    warningBudgetTrimmed:
      'Einige Produkte wurden reduziert oder entfernt, um im Budget zu bleiben:',
    trimmedReduced: 'reduziert {from}→{to}',
    trimmedRemoved: 'entfernt',
    removedBudget: 'entfernt (Budget)',
    partiallyBought: 'nur {to} von {from} Packungen gekauft (Budget)',
    tip: 'Tipp',
    perPack: 'pro Pkg.',
    unitKg: 'kg',
    unitL: 'l',
    unitG: 'g',
    unitMl: 'ml',
    unitPcs: 'Stk.',
    unitPack: 'Pkg.',
    errorUnknownStore:
      'Ein ausgewähltes Geschäft ist für dieses Land nicht verfügbar. Bitte wählen Sie die Geschäfte erneut.',
    errorNotEnoughProducts:
      'Zu wenige Produkte für diese Geschäfte und Ernährungsformen. Fügen Sie ein weiteres Geschäft hinzu oder entfernen Sie eine Ernährungsform.',
    errorNetwork:
      'Der Server ist nicht erreichbar. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    // Share link
    share: 'Teilen',
    shareCopied: 'Link in die Zwischenablage kopiert.',
    shareTooLarge:
      'Dieser Plan ist zu groß für einen Link. Bitte stattdessen kopieren oder drucken.',
    shareFailed: 'Der Link konnte nicht kopiert werden.',
    sharedPlan: 'Geteilter Plan (nur lesen)',
    createYourOwn: 'Eigenen erstellen',
    sharedInvalid: 'Dieser Link ist ungültig oder stammt von einer älteren App-Version.',
  },
};

export type TranslationKey = keyof typeof translations.en;

// Counted nouns, chosen with Intl.PluralRules (see plural.ts). uk: 1 товар, 2 товари,
// 5 товарів, 1,5 товару.
export type PluralNoun = 'item' | 'store' | 'person' | 'day';

export const pluralNouns: Record<Language, Record<PluralNoun, PluralForms>> = {
  en: {
    item: { one: 'item', other: 'items' },
    store: { one: 'store', other: 'stores' },
    person: { one: 'person', other: 'people' },
    day: { one: 'day', other: 'days' },
  },
  uk: {
    item: { one: 'товар', few: 'товари', many: 'товарів', other: 'товару' },
    store: { one: 'магазин', few: 'магазини', many: 'магазинів', other: 'магазину' },
    person: { one: 'особа', few: 'особи', many: 'осіб', other: 'особи' },
    day: { one: 'день', few: 'дні', many: 'днів', other: 'дня' },
  },
  de: {
    item: { one: 'Artikel', other: 'Artikel' },
    store: { one: 'Geschäft', other: 'Geschäfte' },
    person: { one: 'Person', other: 'Personen' },
    day: { one: 'Tag', other: 'Tage' },
  },
};
